import {
  formatJobNumber,
  parseCheckRules,
  parseStationAnswerKey,
  reviewReport,
  reviewStations,
  stationAnswerKey,
} from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Modal } from '../Modal.js'
import { Sheet } from '../Sheet.js'
import { Button, ErrorState, Spinner } from '../ui.js'
import { cn } from '../../lib/cn.js'
import { clearDeviceDraft } from '../../lib/device-drafts.js'
import { downloadUrl, uploadFile } from '../../lib/files.js'
import { addressLine } from '../../lib/format.js'
import { useIsNarrow } from '../../lib/media.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { REPORT_TITLE_ID, ReportFormSurface } from './ReportFormSurface.js'
import { SignaturePad } from './SignaturePad.js'
import { shapeOf, specsOf, stationSpecsOf } from './model.js'
import type { StationActions } from './StationStep.js'
import type { ReportAnswers, ReportPhoto } from './types.js'
import { useAnswerDraft } from './useAnswerDraft.js'

/**
 * Filling in one service's report.
 *
 * A popup over the job card rather than a screen of its own: a report is one
 * service's write-up of the job behind it, and taking the job off screen to
 * fill it in cost the technician the brief, the hazards and the notes they had
 * just been reading. Closing it is a return, not a navigation. Decision 59.
 *
 * It is the second popup in the stack — `JobOverlay` draws it over the job card
 * when the URL carries `?report=`, and closing it leaves the job open behind.
 * Which report is open therefore arrives as a prop and no routing happens in
 * here. Decision 61.
 *
 * Still authenticated, and still behind `FORM_EDIT` for writing — the office can
 * read a report back without being able to change what a technician signed.
 *
 * The verdict on the report is computed here as well as on submission, from the
 * same shared evaluator and the same definition, so the sheet is instant and
 * agrees with what the server records.
 */
export function ReportFormPopup({
  jobId,
  jobServiceId,
  onClose,
}: {
  /** The job behind, for the files a photo or a signature is written against. */
  jobId: string
  jobServiceId: string
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const narrow = useIsNarrow()
  const { can } = useSession()
  const canEdit = can('FORM_EDIT')

  const {
    data: bundle,
    isPending,
    error,
    refetch,
  } = useQuery(trpc.forms.response.queryOptions({ jobServiceId }))

  // The job's own copy of this service changes shape when a report is started or
  // completed, so the card behind is refreshed rather than left stale.
  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries(trpc.forms.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }, [queryClient, trpc])

  const start = useMutation(trpc.forms.startResponse.mutationOptions({ onSuccess: invalidate }))
  const submit = useMutation(
    trpc.forms.submit.mutationOptions({
      onSuccess: (_result, variables) => {
        // The server has the whole report, so the device copy is no longer a
        // safety net — only a copy of a customer's premises on a phone.
        void clearDeviceDraft(variables.responseId)
        invalidate()
      },
    }),
  )
  const reopen = useMutation(trpc.forms.reopen.mutationOptions({ onSuccess: invalidate }))

  /**
   * The PDF, opened in a tab.
   *
   * A mutation because the server may have to render one — a report completed
   * while a bucket was unreachable has no stored document, and asking for it is
   * how the office gets it. `window.open` before the round trip would be
   * blocked as a popup, so the tab is opened on the result and the browser
   * treats it as a download.
   */
  const download = useMutation(
    trpc.forms.document.mutationOptions({
      onSuccess: (document) => {
        const url = downloadUrl({ id: document.attachmentId, checksum: document.checksum })
        if (url) window.open(url, '_blank', 'noopener')
      },
    }),
  )
  const saveAnswers = useMutation(trpc.forms.saveAnswers.mutationOptions())
  const addPhoto = useMutation(trpc.forms.addPhoto.mutationOptions({ onSuccess: invalidate }))
  const setCaption = useMutation(trpc.forms.setPhotoCaption.mutationOptions({ onSuccess: invalidate }))
  const removePhoto = useMutation(trpc.forms.removePhoto.mutationOptions({ onSuccess: invalidate }))
  const addStation = useMutation(trpc.forms.addStation.mutationOptions({ onSuccess: invalidate }))
  const updateStation = useMutation(trpc.forms.updateStation.mutationOptions({ onSuccess: invalidate }))

  const responseId = bundle?.response?.id ?? null

  /**
   * Starting a report is not a decision, so it is no longer a question.
   *
   * Opening one used to land on a screen whose only content was a button
   * reading `Start the report` — the second time of asking in two taps, because
   * the service row on the job card behind says exactly that to get here. The
   * first tap was the intent and this one only made the technician say it
   * again, standing in a roof void. So it starts itself and the wait is a
   * spinner rather than a page.
   *
   * The guard is a ref and not `start.isPending`: a started report stops being
   * null only once `forms.response` has come back, and every render in that gap
   * would otherwise fire another one. `startResponse` is idempotent on the
   * server as well — it hands back the response already on the service — so a
   * race costs a wasted call rather than a duplicate report.
   */
  const startReport = start.mutate
  const autoStarted = useRef<string | null>(null)
  const needsStart =
    bundle !== undefined && bundle.definition !== null && bundle.response === null && canEdit

  useEffect(() => {
    if (!needsStart || autoStarted.current === jobServiceId) return
    autoStarted.current = jobServiceId
    startReport({ jobServiceId })
  }, [needsStart, jobServiceId, startReport])

  const save = useCallback(
    async (answers: Parameters<typeof saveAnswers.mutateAsync>[0]['answers']) => {
      if (responseId === null) return
      await saveAnswers.mutateAsync({ responseId, answers })
    },
    [responseId, saveAnswers],
  )

  const serverAnswers: ReportAnswers = bundle?.answers ?? {}
  const draft = useAnswerDraft({ responseId, serverAnswers, save })

  /**
   * The assists.
   *
   * Two queries, both enabled only once a report exists, because neither has an
   * answer to give about a report nobody has started. They stay separate from
   * `forms.response` on purpose: the filler has to draw before either has
   * arrived, and a suggestion is worth waiting for while a question is not.
   *
   * Both are queries and nothing here writes. A suggestion reaches a field only
   * by the technician ticking it in the sheet, which then goes through the same
   * `saveAnswers` a typed answer does. Decision 55.
   */
  const suggestions = useQuery({
    ...trpc.forms.assists.queryOptions({ responseId: responseId ?? '' }),
    enabled: responseId !== null,
  })

  const history = useQuery({
    ...trpc.forms.fieldHistory.queryOptions({ responseId: responseId ?? '', limit: 4 }),
    enabled: responseId !== null,
  })

  /**
   * Whether talking to this report reaches a transcription service.
   *
   * Asked up front rather than discovered by a refusal, because the microphone
   * and the keyboard make opposite promises about signal and privacy — offering
   * one and delivering the other is the sort of thing that gets a feature
   * switched off. Unconfigured deployments answer `available: false` and the sheet
   * simply has no record button.
   */
  const cloud = useQuery(trpc.forms.dictation.queryOptions())

  /**
   * How many of a spoken pass's suggestions survived review.
   *
   * Fire and forget, and deliberately not awaited anywhere: it is a measurement,
   * the technician has already saved their answers, and a phone that has gone
   * offline between the sheet closing and this call losing a data point is a
   * better outcome than a failed save. Nothing retries it.
   */
  const dictationOutcome = useMutation(trpc.forms.dictationOutcome.mutationOptions())

  /**
   * Photos are not part of the answer draft.
   *
   * They arrive from a camera through their own mutation and carry a caption of
   * their own, so the server owns them and the screen reads them straight from
   * the query. Merging them over the local answers keeps "is this field blank"
   * one question rather than two.
   */
  const photosByField = useMemo(() => {
    const map: Record<string, ReportPhoto[]> = {}
    for (const value of bundle?.response?.values ?? []) {
      // A photo inside a station is keyed by the station too. ADR 029.
      const key = value.assetId ? stationAnswerKey(value.fieldKey, value.assetId) : value.fieldKey
      if (value.photos.length > 0) map[key] = value.photos
    }
    return map
  }, [bundle])

  /**
   * The photo fields, whose answer *is* their attachments.
   *
   * Every other field can carry photos too (decision 73), and there the photos
   * are evidence beside the answer. Merging them over the draft everywhere
   * would replace a typed finding with a list of attachment ids the moment a
   * technician photographed what they had just written about.
   */
  const photoFieldKeys = useMemo(() => {
    const fields = bundle?.definition?.fields ?? []
    return new Set(fields.filter((field) => shapeOf(field) === 'photo').map((field) => field.key))
  }, [bundle])

  const answers = useMemo(() => {
    const merged: ReportAnswers = { ...draft.answers }
    for (const [key, photos] of Object.entries(photosByField)) {
      const field = parseStationAnswerKey(key)?.fieldKey ?? key
      if (photoFieldKeys.has(field)) merged[key] = photos.map((photo) => photo.attachmentId)
    }
    return merged
  }, [draft.answers, photosByField, photoFieldKeys])

  const findings = useMemo(() => {
    if (!bundle?.definition) return []
    return [
      ...reviewReport(specsOf(bundle.definition), answers, parseCheckRules(bundle.definition.checks)),
      // The stations still owed, one line per section, as the server will
      // record them at submission.
      ...reviewStations(stationSpecsOf(bundle.definition, bundle.stations), answers),
    ]
  }, [bundle?.definition, bundle?.stations, answers])

  /**
   * Stations, changed from the step that checks them.
   *
   * A station belongs to the property, so adding one here puts it on the list
   * for every report after this one. The report refetches, because the list it
   * draws comes from the server. ADR 029.
   */
  const stationActions: StationActions | undefined =
    responseId === null
      ? undefined
      : {
          suggestName: async (sectionKey) =>
            (
              await queryClient.fetchQuery({
                ...trpc.forms.nextStationName.queryOptions({ responseId, sectionKey }),
                staleTime: 0,
              })
            ).name,
          add: async (sectionKey, name, location) => {
            await addStation.mutateAsync({ responseId, sectionKey, name, location })
          },
          update: async (assetId, patch) => {
            await updateStation.mutateAsync({ assetId, ...patch })
          },
        }

  /**
   * Capture.
   *
   * A photo goes to storage first and onto the field second, because the
   * attachment is the durable thing: an upload that succeeds and then fails to
   * attach leaves a file on the job, which somebody can find. The reverse would
   * leave a field pointing at nothing.
   */
  const picker = useRef<HTMLInputElement>(null)
  const photoField = useRef<string | null>(null)
  const [signing, setSigning] = useState<{ key: string; label: string } | null>(null)
  const [captureError, setCaptureError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const takePhotos = async (files: FileList) => {
    const fieldKey = photoField.current
    if (fieldKey === null || responseId === null) return

    setUploading(true)
    setCaptureError(null)

    try {
      for (const file of files) {
        const attachment = await uploadFile(file, { jobId, kind: 'PHOTO' })
        await addPhoto.mutateAsync({ responseId, fieldKey, attachmentId: attachment.id })
      }
    } catch (cause) {
      setCaptureError(cause instanceof Error ? cause.message : 'That photo did not upload.')
    } finally {
      setUploading(false)
    }
  }

  const takeSignature = async (png: Blob) => {
    if (signing === null || responseId === null) return

    setUploading(true)
    setCaptureError(null)

    try {
      const attachment = await uploadFile(png, {
        jobId,
        filename: `${signing.key}.png`,
        kind: 'SIGNATURE',
      })
      draft.set(signing.key, attachment.id)
      setSigning(null)
    } catch (cause) {
      setCaptureError(cause instanceof Error ? cause.message : 'That signature did not save.')
    } finally {
      setUploading(false)
    }
  }

  const exit = onClose

  if (isPending) {
    return (
      <ReportPopup narrow={narrow} compact onClose={exit}>
        <div className="grid place-items-center p-10">
          <h1 id={REPORT_TITLE_ID} className="sr-only">
            Report
          </h1>
          <Spinner className="size-6 text-ink-400" />
        </div>
      </ReportPopup>
    )
  }

  if (error) {
    return (
      <ReportPopup narrow={narrow} compact onClose={exit}>
        <div className="grid place-items-center p-8">
          <h1 id={REPORT_TITLE_ID} className="sr-only">
            Report
          </h1>
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        </div>
      </ReportPopup>
    )
  }

  if (!bundle) return null

  const { service, definition, response } = bundle
  const subtitle = [
    `Job ${formatJobNumber(service.job)}`,
    service.job.site?.name ?? addressLine(service.job.site?.address),
  ]
    .filter((part) => part && part !== '—')
    .join(' · ')

  if (definition === null) {
    return (
      <Standing
        narrow={narrow}
        title={`${service.serviceType.name} does not produce a report`}
        body="Nothing on this service needs writing up. Note anything worth keeping against the service on the job card instead."
        onExit={exit}
      />
    )
  }

  if (response === null) {
    // Nothing the office can do would start it, so they are told rather than
    // left watching a spinner that is never going to resolve.
    if (!canEdit) {
      return (
        <Standing
          narrow={narrow}
          title={definition.form.name}
          body="Nobody has started this report yet. A technician on the job starts it from their own device."
          onExit={exit}
        />
      )
    }

    // The one case that still needs a button. Starting failed, so the report
    // cannot draw, and retrying is the only thing left worth offering.
    if (start.error) {
      return (
        <Standing
          narrow={narrow}
          title={definition.form.name}
          body="This report did not start."
          error={errorMessage(start.error)}
          onExit={exit}
          action={
            <Button
              variant="primary"
              size="lg"
              loading={start.isPending}
              onClick={() => start.mutate({ jobServiceId })}
            >
              Try again
            </Button>
          }
        />
      )
    }

    return (
      <ReportPopup narrow={narrow} compact onClose={exit}>
        <div className="grid place-items-center p-10">
          <h1 id={REPORT_TITLE_ID} className="sr-only">
            {definition.form.name}
          </h1>
          <Spinner className="size-6 text-ink-400" />
        </div>
      </ReportPopup>
    )
  }

  const readOnly = response.status !== 'DRAFT' || !canEdit

  return (
    <>
      <ReportPopup narrow={narrow} onClose={exit}>
        <ReportFormSurface
          title={definition.form.name}
          subtitle={subtitle}
          definition={definition}
          answers={answers}
          photosByField={photosByField}
          findings={findings}
          saveState={draft.state}
          savePersisted={draft.persisted}
          recovered={draft.recovered}
          readOnly={readOnly}
          submitting={submit.isPending}
          captureError={captureError ?? uploadNotice(uploading)}
          suggestions={suggestions.data ?? []}
          suggestionsLoading={suggestions.isFetching}
          {...(history.data ? { history: history.data } : {})}
          responseId={response.id}
          {...(cloud.data?.available ? { cloud: cloud.data } : {})}
          onChange={draft.set}
          onChangeMany={draft.setMany}
          onDictationOutcome={(runId, accepted) => dictationOutcome.mutate({ runId, accepted })}
          onComplete={() => {
            // Anything still on the idle timer goes first, so the verdict recorded
            // with the submission is the one the technician was shown.
            void draft.flush().then(() => submit.mutate({ responseId: response.id }))
          }}
          {...(canEdit ? { onReopen: () => reopen.mutate({ responseId: response.id }) } : {})}
          onDownload={() => download.mutate({ responseId: response.id })}
          stations={bundle.stations}
          stationHistory={bundle.stationHistory}
          {...(readOnly || stationActions === undefined ? {} : { stationActions })}
          downloading={download.isPending}
          downloadError={download.error ? errorMessage(download.error) : null}
          {...(narrow
            ? // The sheet draws its own Done. A second way out in the header
              // would be two of them a thumb's width apart.
              {}
            : { onExit: exit })}
          {...(readOnly
            ? {}
            : {
                capture: {
                  addPhoto: (fieldKey: string) => {
                    photoField.current = fieldKey
                    picker.current?.click()
                  },
                  removePhoto: (photoId: string) => removePhoto.mutate({ photoId }),
                  captionPhoto: (photoId: string, caption: string) =>
                    setCaption.mutate({ photoId, caption: caption === '' ? null : caption }),
                  sign: (fieldKey: string) =>
                    setSigning({
                      key: fieldKey,
                      label:
                        definition.fields.find((field) => field.key === fieldKey)?.label ??
                        'Signature',
                    }),
                },
              })}
        />
      </ReportPopup>

      {/* Outside the surface so it survives a re-render mid-pick, and
          `capture="environment"` so a phone opens the rear camera rather than
          the photo library. */}
      <input
        ref={picker}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        data-testid="photo-picker"
        onChange={(event) => {
          const { files } = event.target
          if (files && files.length > 0) void takePhotos(files)
          // Reset, or picking the same file twice in a row fires nothing.
          event.target.value = ''
        }}
      />

      <SignaturePad
        open={signing !== null}
        title={signing?.label ?? 'Signature'}
        saving={uploading}
        error={captureError}
        onClose={() => {
          setSigning(null)
          setCaptureError(null)
        }}
        onSave={(png) => void takeSignature(png)}
      />
    </>
  )
}

/**
 * What the filler is drawn in.
 *
 * A modal at a desk and a sheet on a phone, the same pair the job card presents
 * as and for the same reason (decision 58): a centred dialog on a 390px screen
 * is only a page with a margin, and the report's own footer is that screen's
 * bottom bar while it is open. `compact` is for the states that are a sentence
 * and a button — a dialog sized for a 28-question report around three lines of
 * text reads as a screen that failed to load.
 */
function ReportPopup({
  narrow,
  compact = false,
  onClose,
  children,
}: {
  narrow: boolean
  compact?: boolean
  onClose: () => void
  children: ReactNode
}) {
  if (narrow) {
    return (
      <Sheet
        size={compact ? 'auto' : 'tall'}
        scrollBody={false}
        labelledBy={REPORT_TITLE_ID}
        onClose={onClose}
      >
        {children}
      </Sheet>
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      labelledBy={REPORT_TITLE_ID}
      className={cn(
        'min-w-0 rounded-[18px]',
        compact ? 'max-w-[440px]' : 'h-[min(900px,calc(100vh-2rem))] max-w-[900px]',
      )}
    >
      {children}
    </Modal>
  )
}

/** Told plainly, because an upload over a poor connection is a long silence. */
function uploadNotice(uploading: boolean): string | null {
  return uploading ? 'Uploading…' : null
}

/** The filler with nothing to fill: no form, or no report started. */
function Standing({
  narrow,
  title,
  body,
  action,
  error,
  onExit,
}: {
  narrow: boolean
  title: string
  body: string
  action?: React.ReactNode
  error?: string
  onExit: () => void
}) {
  return (
    <ReportPopup narrow={narrow} compact onClose={onExit}>
      <section
        aria-label="Report"
        className="flex flex-col items-center gap-3 p-6 text-center"
      >
        <h1 id={REPORT_TITLE_ID} className="text-[15px] font-semibold text-ink-900">
          {title}
        </h1>
        <p className="text-[13px] leading-relaxed text-ink-600">{body}</p>
        {error ? <p className="text-xs text-red-700">{error}</p> : null}
        {action}
        <Button variant="ghost" size="sm" onClick={onExit}>
          Back to the job
        </Button>
      </section>
    </ReportPopup>
  )
}
